import React, { useState } from 'react';
import { LoginUser } from "../../Utils/Api";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from "react-router-dom";

const Login = ({ onLogin }) => {
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();

    // Handle input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (error) setError('');
    };

    // Process successful login
    const processLogin = (data, token) => {
        sessionStorage.setItem('AuthUser', JSON.stringify(data));
        sessionStorage.setItem('token', token);

        if (onLogin) onLogin();

        toast.success('Login successful!', {
            position: "top-right",
            autoClose: 2000,
        });

        setTimeout(() => navigate('/dashboards'), 2000);
    };

    // Handle login submission
    const handleLogin = async (e) => {
        e?.preventDefault();

        // Validation
        if (!formData.email || !formData.password) {
            setError('Please fill in all fields');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const response = await LoginUser(formData);
            const apiData = response?.data;
            
            // Check different response formats
            const user = apiData?.user || apiData?.data?.user;
            const token = apiData?.accessToken || apiData?.token || apiData?.data?.token;

            if (user && token) {
                // Normalize data to ensure 'token' exists for the interceptor
                const normalizedData = { 
                    ...apiData, 
                    user: user,
                    token: token 
                };
                processLogin(normalizedData, token);
            } else {
                setError("Invalid credentials");
                toast.error('Invalid credentials');
            }
        } catch (err) {
            console.error('Login error:', err);
            setError(err.message || "Something went wrong. Please try again.");
            toast.error('Login failed. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    // Handle Enter key press
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !isLoading) {
            handleLogin();
        }
    };

    return (
        <div>
            <ToastContainer />
            <div className="row authentication mx-0">
                <div className="col-xxl-7 col-xl-7 col-lg-12">
                    <div className="row justify-content-center align-items-center h-100">
                        <div className="col-xxl-6 col-xl-7 col-lg-7 col-md-7 col-sm-8 col-12">
                            <div className="p-5">
                                {/* Logo Section */}
                                <div className="mb-3">
                                    <a href="/">
                                        <img
                                            src="New/assets/images/brand-logos/desktop-logo.png"
                                            alt="logo"
                                            className="authentication-brand desktop-logo"
                                        />
                                        <img
                                            src="New/assets/images/brand-logos/desktop-dark.png"
                                            alt="logo"
                                            className="authentication-brand desktop-dark"
                                        />
                                    </a>
                                </div>

                                {/* Header */}
                                <h2 className="h5 fw-semibold mb-2">Log In</h2>
                                <p className="mb-3 text-muted op-7 fw-normal">Welcome back SkillaGig!</p>

                                {/* Error Display */}
                                {error && (
                                    <div className="alert alert-danger py-2" role="alert">
                                        <small>{error}</small>
                                    </div>
                                )}

                                {/* Login Form */}
                                <form onSubmit={handleLogin}>
                                    <div className="row gy-3">
                                        {/* Email Field */}
                                        <div className="col-xl-12 mt-0">
                                            <label htmlFor="signin-email" className="form-label text-default">
                                                Email
                                            </label>
                                            <input
                                                type="email"
                                                className="form-control form-control-lg"
                                                id="signin-email"
                                                name="email"
                                                placeholder="Enter your email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                onKeyPress={handleKeyPress}
                                                disabled={isLoading}
                                                required
                                            />
                                        </div>

                                        {/* Password Field */}
                                        <div className="col-xl-12 mb-3">
                                            <label htmlFor="signin-password" className="form-label text-default d-block">
                                                Password
                                            </label>
                                            <div className="input-group">
                                                <input
                                                    type={showPassword ? "text" : "password"}
                                                    className="form-control form-control-lg"
                                                    id="signin-password"
                                                    name="password"
                                                    placeholder="password"
                                                    value={formData.password}
                                                    onChange={handleInputChange}
                                                    onKeyPress={handleKeyPress}
                                                    disabled={isLoading}
                                                    required
                                                />
                                                <button
                                                    className="btn btn-light"
                                                    type="button"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    disabled={isLoading}
                                                >
                                                    <i className={`ri-eye${showPassword ? '' : '-off'}-line align-middle`} />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Submit Button */}
                                        <div className="col-xl-12 d-grid mt-2">
                                            <button
                                                type="submit"
                                                className="btn btn-lg btn-primary"
                                                disabled={!formData.email || !formData.password || isLoading}
                                            >
                                                {isLoading ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                        Signing In...
                                                    </>
                                                ) : (
                                                    'Sign In'
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right-side image section */}
                <div className="col-xxl-5 col-xl-5 col-lg-5 d-xl-block d-none px-0">
                    <div className="authentication-cover">
                        <div className="aunthentication-cover-content rounded">
                            <div className="swiper keyboard-control">
                                <div className="swiper-wrapper">
                                    <div className="swiper-slide">
                                        <div className="text-fixed-white text-center p-5 d-flex align-items-center justify-content-center">
                                            <div>
                                                <div className="mb-5">
                                                    <img
                                                        src="New/assets/images/authentication/2.png"
                                                        className="authentication-image"
                                                        alt=""
                                                    />
                                                </div>
                                                <h6 className="fw-semibold text-fixed-white">Sign In</h6>
                                                <p className="fw-normal fs-14 op-7">
                                                    Share your knowledge and help others learn. Connect with students who need guidance in academics, languages, music, and specialized skills.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
